/**
 * MarkdownRenderer
 * - ```charts [...] ``` 블록 → extractChartsAndText로 분리 (부모가 ChartJsonBlock 렌더링)
 * - 마크다운 테이블 → md-table-scroll 래퍼 + 엑셀 다운로드 버튼
 * - 나머지 텍스트는 marked로 HTML 렌더링
 */

import { useEffect, useRef } from 'react';
import { marked, Renderer } from 'marked';
import * as XLSX from 'xlsx';
export type { ChartSpec } from './ChartJsonBlock';
import type { ChartSpec } from './ChartJsonBlock';

// ── marked 커스텀 렌더러 (테이블 래퍼 + 엑셀 버튼) ─────────────────────────
const renderer = new Renderer();
let tableIdx = 0; // 페이지 내 테이블 인덱스 (이벤트 바인딩용)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
(renderer as any).table = (token: any) => {
  const idx = tableIdx++;
  const headerHtml = (token.header as any[]).map((cell: any) =>
    `<th>${marked.parseInline(String(cell.text ?? cell))}</th>`
  ).join('');

  const bodyHtml = (token.rows as any[][]).map((row: any[]) => {
    const cells = row.map((cell: any) =>
      `<td>${marked.parseInline(String(cell.text ?? cell))}</td>`
    ).join('');
    return `<tr>${cells}</tr>`;
  }).join('');

  return `<div class="md-table-scroll">
    <table data-table-idx="${idx}"><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>
    <div class="chat-chart-actions">
      <button class="chat-chart-dl-btn" data-table-idx="${idx}">엑셀 다운로드</button>
    </div>
  </div>`;
};

const markedInstance = marked.use({ renderer });

// ── charts 블록 파싱 ────────────────────────────────────────────────────────
function parseChartsBlock(raw: string): ChartSpec[] | null {
  try {
    const parsed = JSON.parse(raw.trim());
    if (Array.isArray(parsed)) return parsed as ChartSpec[];
  } catch { /* ignore */ }
  return null;
}

// ── charts 블록 제거 후 순수 마크다운 텍스트 반환 ───────────────────────────
export function extractChartsAndText(content: string): {
  text: string;
  chartGroups: ChartSpec[][];
} {
  const chartGroups: ChartSpec[][] = [];
  const text = content.replace(/```charts\s*\n?([\s\S]*?)```/gi, (_, json) => {
    const specs = parseChartsBlock(json);
    if (specs && specs.length > 0) chartGroups.push(specs);
    return '';
  }).trim();
  return { text, chartGroups };
}

// ── 엑셀 다운로드 ────────────────────────────────────────────────────────────
function downloadTableExcel(table: HTMLTableElement) {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.table_to_sheet(table);
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fileName = `table_${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;
  XLSX.writeFile(wb, fileName);
}

// ── 마크다운 → HTML 변환 ────────────────────────────────────────────────────
interface MarkdownRendererProps {
  content: string;
  className?: string;
}

export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { text } = extractChartsAndText(content);

  useEffect(() => {
    if (!ref.current) return;
    tableIdx = 0; // 렌더링마다 리셋
    try {
      const html = markedInstance(text);
      ref.current.innerHTML = typeof html === 'string' ? html : '';
    } catch {
      ref.current.innerHTML = text.replace(/\n/g, '<br>');
    }

    // 엑셀 버튼 이벤트 바인딩
    ref.current.querySelectorAll<HTMLButtonElement>('.chat-chart-dl-btn[data-table-idx]').forEach(btn => {
      btn.addEventListener('click', () => {
        const idx = btn.dataset.tableIdx;
        const table = ref.current?.querySelector<HTMLTableElement>(`table[data-table-idx="${idx}"]`);
        if (table) downloadTableExcel(table);
      });
    });
  }, [text]);

  if (!text.trim()) return null;
  return <div ref={ref} className={`markdown-content${className ? ` ${className}` : ''}`} />;
}
